import React from 'react';
import { Close } from '@mui/icons-material';
import { useFxRates } from '../../../hooks/useFxRates';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { getCurrencySymbol } from '../../../hooks/useCurrencyRates';

// Currency display names
const CURRENCY_NAMES: Record<string, string> = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  ILS: 'Israeli Shekel',
  CNY: 'Chinese Yuan',
  JPY: 'Japanese Yen',
};

const CURRENCY_COLORS: Record<string, string> = {
  USD: '#3b82f6',
  EUR: '#6366f1',
  GBP: '#10b981',
  ILS: '#f59e0b',
  CNY: '#ef4444',
  JPY: '#ec4899',
};

const DEFAULT_COLOR = '#94a3b8';

export const FxRatesDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const baseCurrency = useBaseCurrency();
  const { rates, asOf } = useFxRates(baseCurrency);

  const sortedPairs = React.useMemo(() => {
    return Object.entries(rates)
      .filter(([, rate]) => typeof rate === 'number' && rate > 0)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [rates]);

  const asOfLabel = React.useMemo(() => {
    if (!asOf) return null;
    const d = new Date(asOf);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays <= 6) return `${diffDays} days ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }, [asOf]);

  return (
      <div className="flex flex-col bg-white rounded-lg w-[320px] max-w-[90vw]">
        {/* Header */}
        <div className="flex items-start justify-between px-4 py-2.5 border-b border-gray-100">
          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-900">FX Rates</span>
            <span className="text-[11px] text-gray-400">
              1 {baseCurrency} equals
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors shrink-0 -mr-1 -mt-0.5"
            aria-label="Close"
          >
            <Close className="text-gray-400 hover:text-gray-600" sx={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Rate list */}
        <div className="px-3 py-2.5 flex flex-col gap-1">
          {sortedPairs.length > 0 ? (
            sortedPairs.map(([curr, rate]) => (
              <div
                key={curr}
                className="flex items-center px-3 py-2 bg-gray-50 rounded-lg"
              >
                <div
                  className="w-2.5 h-2.5 rounded-full shrink-0 mr-3"
                  style={{ backgroundColor: CURRENCY_COLORS[curr] || DEFAULT_COLOR }}
                />
                <span className="text-sm font-bold text-gray-800 w-10">{curr}</span>
                <span className="text-xs text-gray-400 flex-1">
                  {CURRENCY_NAMES[curr] || curr}
                </span>
                <span className="text-sm font-bold text-gray-900 tabular-nums">
                  {getCurrencySymbol(curr)}{' '}
                  {rate.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: rate >= 100 ? 2 : 4,
                  })}
                </span>
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
              No FX rates available
            </div>
          )}
        </div>
        {asOfLabel && (
          <div className="px-4 pb-2.5 text-[10px] text-gray-400 text-right">
            Updated {asOfLabel}
          </div>
        )}
      </div>
  );
};
